/** The three mutually exclusive freight default columns. */
export const FREIGHT_TOGGLE_ALIASES = ['freightPercentage', 'freightValue', 'freightAutomated'];

/** The column every other freight option falls back to when the active one is switched off. */
const DEFAULT_FALLBACK_ALIAS = 'freightPercentage';

/** Where Freight % itself falls back to when it is the one switched off. */
const FREIGHT_PERCENTAGE_FALLBACK_ALIAS = 'freightValue';

/** The default-column fields the toggle plan reads. */
export interface FreightToggleColumn {
  _id?: string;
  aliasName?: string;
  isActive?: boolean;
}

/** One active-flag write the template page sends. */
export interface FreightToggleUpdate {
  columnId: string;
  isActive: boolean;
}

/**
 * Plans the active-flag writes for switching one default column on or off. Freight %, Freight Value
 * and Freight Automated are mutually exclusive: switching one on switches the other two off, and
 * switching the active one off falls back to Freight % — or to Freight Value when Freight % is the
 * one switched off. Any other column is a plain single write.
 * @param columns The template's default columns.
 * @param target The column being switched.
 * @param isActive The target's new state.
 * @returns The writes, the target's first, without any that would not change a column.
 */
export function planFreightToggle(columns: ReadonlyArray<FreightToggleColumn>, target: FreightToggleColumn, isActive: boolean): FreightToggleUpdate[] {
  const targetId = String(target?._id ?? '');
  const updates: FreightToggleUpdate[] = [{ columnId: targetId, isActive }];
  if (!FREIGHT_TOGGLE_ALIASES.includes(String(target?.aliasName))) return updates;

  const others = columns.filter((column) => FREIGHT_TOGGLE_ALIASES.includes(String(column?.aliasName)) && String(column?._id) !== targetId);
  const fallbackAlias = target.aliasName === DEFAULT_FALLBACK_ALIAS ? FREIGHT_PERCENTAGE_FALLBACK_ALIAS : DEFAULT_FALLBACK_ALIAS;
  others.forEach((column) => {
    const shouldBeActive = !isActive && column.aliasName === fallbackAlias;
    if (Boolean(column.isActive) !== shouldBeActive) updates.push({ columnId: String(column._id), isActive: shouldBeActive });
  });
  return updates;
}
